package ai.wagemix.logic.analytics

import ai.wagemix.data.WMAuthSyncEvent
import ai.wagemix.data.touchesUser
import ai.wagemix.logic.WMBaseDataGateway
import ai.wagemix.logic.WMSessionDataGateway
import com.fsryan.foundation.models.FSStringEnumerated
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.transform
import kotlin.time.Instant

/**
 * One login session (planning/analytics-plan.md §3): from a sign-in to the sign-out, regardless of activity. Its
 * [id] is the `wm_login_session_id` of every event the user's actions produce meanwhile.
 */
interface WMLoginSession {

    /** The Firebase uid of the signed-in user the session belongs to. */
    val uid: String

    /** The session's id, a UUID. */
    val id: String

    /** When the session was minted. */
    val startedAt: Instant

    /** Anchors the factory. */
    companion object
}

/**
 * Creates a [WMLoginSession].
 *
 * @param uid the Firebase uid of the signed-in user
 * @param id the session's id
 * @param startedAt when the session was minted
 * @return the session
 */
fun WMLoginSession(uid: String, id: String, startedAt: Instant): WMLoginSession {
    return WMLoginSessionData(uid = uid, id = id, startedAt = startedAt)
}

private data class WMLoginSessionData(
    override val uid: String,
    override val id: String,
    override val startedAt: Instant,
): WMLoginSession

/** How a visitor became signed in during this visit (`user_identified.method`). */
enum class WMSignInMethod(override val stringValue: String): FSStringEnumerated {
    Login("login"),
    Register("register"),
}

/** What the site does to its login session store when the signed-in user changes. */
sealed interface WMLoginSessionChange {

    /** The stored session, if any, still holds. */
    data object None: WMLoginSessionChange

    /**
     * The stored [session] ends and is removed. When [signedOut] is true the sign-out happened during this visit, so
     * `signed_out` is sent first; otherwise the session is stale (signed out elsewhere) and is removed silently.
     */
    @ConsistentCopyVisibility
    data class Ended internal constructor(val session: WMLoginSession, val signedOut: Boolean): WMLoginSessionChange

    /**
     * A new [session] is stored, replacing any other, and `login_session_started` is sent. [restored] is true when a
     * signed-in user arrived without a stored session; otherwise [method] says how the user signed in, and
     * `user_identified` is sent too.
     */
    @ConsistentCopyVisibility
    data class Started internal constructor(
        val session: WMLoginSession,
        val restored: Boolean,
        val method: WMSignInMethod?,
    ): WMLoginSessionChange

    /** Anchors [of]. */
    companion object {

        /**
         * Decides what happens to the login session store when the signed-in user is observed.
         *
         * @param stored the session in the store, or null
         * @param previousUid the uid observed before, during this visit, or null (nobody, or the first observation)
         * @param currentUid the uid now signed in, or null when nobody is
         * @param pendingMethod how a sign-in or registration in progress was started, or null when none is
         * @param now the time of the observation, the start of a minted session
         * @param newSessionId mints a session id; called only when a session is started
         * @return the change
         */
        fun of(
            stored: WMLoginSession?,
            previousUid: String?,
            currentUid: String?,
            pendingMethod: WMSignInMethod?,
            now: Instant,
            newSessionId: () -> String,
        ): WMLoginSessionChange {
            if (currentUid == null) {
                return when (stored) {
                    null -> None
                    else -> Ended(session = stored, signedOut = previousUid != null)
                }
            }
            if (pendingMethod != null) {
                val session = WMLoginSession(uid = currentUid, id = newSessionId(), startedAt = now)
                return Started(session = session, restored = false, method = pendingMethod)
            }
            if (stored?.uid == currentUid) {
                return None
            }
            val session = WMLoginSession(uid = currentUid, id = newSessionId(), startedAt = now)
            return Started(session = session, restored = true, method = null)
        }
    }
}

/**
 * The sign-ins and registrations started in this page: [WMSignInMethod.Login] or [WMSignInMethod.Register] when one
 * starts, before its user is signed in, and null when it fails. A success emits nothing: the user it signs in is
 * observed through the cache.
 */
fun WMSessionDataGateway.signInAttemptFlow(): Flow<WMSignInMethod?> {
    return authSyncEventFlow.transform { event ->
        when (event) {
            is WMAuthSyncEvent.SignIn.Initial -> emit(WMSignInMethod.Login)
            is WMAuthSyncEvent.Register.Initial -> emit(WMSignInMethod.Register)
            is WMAuthSyncEvent.SignIn.Failure, is WMAuthSyncEvent.Register.Failure -> emit(null)
            else -> Unit
        }
    }
}

/**
 * The uid of the signed-in user, or null while nobody is signed in: the current value first, then one value per
 * cache write that touches a user, repeated values included, so that a sign-in as the user already signed in is seen.
 */
fun WMBaseDataGateway.signedInUidFlow(): Flow<String?> {
    return cacheUpdateFlow
        .filter { it.touchesUser() }
        .map { retrieveActiveUser()?.uid }
        .onStart { emit(retrieveActiveUser()?.uid) }
}
